import React, { useEffect, useState, useCallback } from 'react';
import { adminUserService } from '../../services/admin-user.service';
import { Button } from '../common/Button';
import { CreateAccountModal } from './CreateAccountModal';
import { LockAccountConfirmModal } from './LockAccountConfirmModal';
import { DeleteAccountConfirmModal } from './DeleteAccountConfirmModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import type { AdminUserView, CreateUserInput } from '../../types/admin-user.types';
import type { AccountStatus } from '../../types/auth.types';
import { useAuth } from '../../hooks/useAuth';

type Modal =
  | { type: 'create' }
  | { type: 'lock'; account: AdminUserView; targetStatus: AccountStatus }
  | { type: 'password'; account: AdminUserView }
  | { type: 'delete'; account: AdminUserView };

export function AdminAccountManagement(): React.ReactElement {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminUserService.listUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleCreate = async (input: CreateUserInput) => {
    const newUser = await adminUserService.createUser(input);
    setUsers(prev => [newUser, ...prev]);
    setModal(null);
    setSuccess(`Account "${newUser.name || newUser.email}" created successfully.`);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleStatusUpdate = async () => {
    if (modal?.type !== 'lock') return;
    const updated = await adminUserService.updateStatus(modal.account.id, { status: modal.targetStatus });
    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
    setModal(null);
    setSuccess(`Account status updated to ${updated.status}.`);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handlePasswordUpdate = async (newPassword: string) => {
    if (modal?.type !== 'password') return;
    const targetAccount = modal.account;
    await adminUserService.updatePassword(targetAccount.id, { password: newPassword });
    setModal(null);
    setSuccess(`Password for "${targetAccount.name || targetAccount.email}" updated successfully.`);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleDelete = async () => {
    if (modal?.type !== 'delete') return;
    const { id } = await adminUserService.deleteUser(modal.account.id);
    setUsers(prev => prev.filter(u => u.id !== id));
    setModal(null);
    setSuccess('Account deleted successfully.');
    setTimeout(() => setSuccess(null), 5000);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-white">Account Management</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {users.length} account{users.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <Button
          id="admin-create-account-btn"
          size="sm"
          onClick={() => {
            setError(null);
            setSuccess(null);
            setModal({ type: 'create' });
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Account
        </Button>
      </div>

      {/* Success banner */}
      {success && (
        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {success}
          <button className="ml-auto text-slate-400 hover:text-white" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
          </svg>
          {error}
          <button className="ml-auto text-slate-400 hover:text-white" onClick={() => { void loadUsers(); }}>Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="py-8 flex items-center justify-center gap-2 text-slate-400 text-sm">
          <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Loading accounts…
        </div>
      )}

      {/* Account table */}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm" id="admin-accounts-table">
            <thead>
              <tr className="bg-white/5 text-left">
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Account</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:table-cell">Role</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">Words</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider hidden lg:table-cell">Created</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-sm">
                    No accounts found.
                  </td>
                </tr>
              )}
              {users.map(account => {
                const isSelf = account.id === currentUser?.id;
                const isActive = account.status === 'ACTIVE';
                return (
                  <tr key={account.id} className="hover:bg-white/3 transition-colors">
                    {/* Account */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-600 to-accent-600 flex items-center justify-center text-white text-xs font-bold shadow-sm shrink-0">
                          {(account.name?.[0] ?? account.email[0] ?? '?').toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-white leading-tight truncate">
                            {account.name || account.email}
                            {isSelf && (
                              <span className="ml-1.5 text-xs text-primary-400 font-normal">(you)</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{account.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        account.role === 'ADMIN'
                          ? 'bg-violet-500/15 text-violet-400 border border-violet-500/25'
                          : 'bg-slate-500/15 text-slate-400 border border-slate-500/25'
                      }`}>
                        {account.role}
                      </span>
                    </td>

                    {/* Word count */}
                    <td className="px-4 py-3 hidden md:table-cell text-slate-300">
                      {account.wordCount}
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs whitespace-nowrap">
                      {formatDate(account.createdAt)}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isActive
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                          : 'bg-red-500/15 text-red-400 border border-red-500/25'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        {account.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Change Password */}
                        <button
                          id={`admin-change-password-${account.id}`}
                          title="Change password"
                          onClick={() => {
                            setError(null);
                            setSuccess(null);
                            setModal({ type: 'password', account });
                          }}
                          className="p-1.5 rounded-lg text-primary-400 hover:text-primary-300 hover:bg-primary-500/10 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>

                        {/* Lock / Unlock — disabled for self */}
                        <button
                          id={`admin-toggle-status-${account.id}`}
                          disabled={isSelf}
                          title={isSelf ? 'Cannot lock your own account' : isActive ? 'Lock account' : 'Unlock account'}
                          onClick={() => {
                            setError(null);
                            setSuccess(null);
                            setModal({
                              type: 'lock',
                              account,
                              targetStatus: isActive ? 'LOCKED' : 'ACTIVE',
                            });
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isSelf
                              ? 'opacity-30 cursor-not-allowed'
                              : isActive
                              ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
                              : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                          }`}
                        >
                          {isActive ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>

                        {/* Delete — disabled for self */}
                        <button
                          id={`admin-delete-${account.id}`}
                          disabled={isSelf}
                          title={isSelf ? 'Cannot delete your own account' : 'Delete account'}
                          onClick={() => {
                            setError(null);
                            setSuccess(null);
                            setModal({ type: 'delete', account });
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            isSelf
                              ? 'opacity-30 cursor-not-allowed'
                              : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'create' && (
        <CreateAccountModal
          onClose={() => setModal(null)}
          onConfirm={handleCreate}
        />
      )}
      {modal?.type === 'lock' && (
        <LockAccountConfirmModal
          account={modal.account}
          targetStatus={modal.targetStatus}
          onClose={() => setModal(null)}
          onConfirm={handleStatusUpdate}
        />
      )}
      {modal?.type === 'password' && (
        <ChangePasswordModal
          account={modal.account}
          onClose={() => setModal(null)}
          onConfirm={handlePasswordUpdate}
        />
      )}
      {modal?.type === 'delete' && (
        <DeleteAccountConfirmModal
          account={modal.account}
          onClose={() => setModal(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
